import type {
  FunctionRole,
  NotificationCadence,
  NotificationChannel,
  NotificationLocale,
  NotificationRecipientRole,
  PermissionUser,
  Phase,
  PhaseDueMeaning,
  PromptListItem,
  Segment,
  Tag,
  User,
} from '@projektverwaltung/shared';
import { canManageTagInScope } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FunctionRoleEditor,
  shouldShowNoRolesHint,
  useFunctionRoleMutations,
} from '../components/admin/function-role-editor.js';
import { SegmentWhitelistEditor, SyncScheduleCard } from '../components/sync-schedule-card.js';
import { useToast } from '../components/toast.js';
import { useConfirm } from '../components/v2/confirm-dialog.js';
import {
  ADMIN_LOADING_FALLBACK_ACTOR,
  type AdminTab,
  assignableSegments,
  canManageUserAccounts,
  defaultAdminTab,
  isKuseAdminOnly,
  visibleAdminTabs,
} from '../lib/admin-gating.js';
import {
  type AdminUser,
  type DigestSettingsInput,
  type NotificationRuleListItem,
  type NotificationTemplateListItem,
  api,
} from '../lib/api.js';
import { FLAGS_QUERY_KEY, useFeatureFlags } from '../lib/features.js';
import { formatZeitpunkt } from '../lib/format-date.js';
import { anzeigeName } from '../lib/nutzer-anzeige.js';
import { useSession } from '../lib/session.js';
import { ActionTextsAdmin } from './admin-action-texts.js';
import { NeueNutzerAdmin } from './admin-neue-nutzer.js';
import { NewsAdmin } from './admin-news.js';
import { ProjektPruefungenAdmin } from './admin-projekt-pruefungen.js';

export function AdminPage() {
  const { t } = useTranslation();
  const { user: sessionUser } = useSession();
  const actor = sessionUser ?? ADMIN_LOADING_FALLBACK_ACTOR;
  const allowedTabs = visibleAdminTabs(actor);
  // Rücksprung aus dem Float-Login (PV-052 L4) landet auf `/admin?tab=notifications` — ein
  // erlaubter Reiter aus der URL gewinnt gegen den Standard.
  const [tab, setTab] = useState<AdminTab>(() => {
    const ausUrl = new URLSearchParams(window.location.search).get('tab');
    return ausUrl && allowedTabs.includes(ausUrl as AdminTab)
      ? (ausUrl as AdminTab)
      : defaultAdminTab(actor);
  });

  const ALL_TABS: { key: AdminTab; label: string }[] = [
    { key: 'users', label: t('admin.tab_users', 'Benutzer & Rollen') },
    { key: 'tags', label: t('admin.tab_tags', 'Tags / Labels') },
    { key: 'phases', label: t('admin.tab_phases', 'Phasen') },
    { key: 'salesforce', label: t('admin.tab_salesforce', 'Salesforce') },
    { key: 'flags', label: t('admin.tab_flags', 'Feature-Flags') },
    { key: 'notifications', label: t('admin.tab_notifications', 'Benachrichtigungen') },
    { key: 'actions', label: t('admin.tab_actions', 'Handlungshinweise') },
    { key: 'prompts', label: t('admin.tab_prompts', 'Systemprompts') },
    { key: 'news', label: t('admin.tab_news', 'News') },
    { key: 'neue_nutzer', label: t('admin.tab_neue_nutzer', 'Neue Nutzer') },
  ];
  // F2: KuSe-Admin (ohne is_admin) sieht nur users + tags + neue_nutzer — visibleAdminTabs() filtert.
  const tabs = ALL_TABS.filter((tb) => allowedTabs.includes(tb.key));

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--ink)]">
          {t('admin.heading')}
        </h1>
      </div>
      {isKuseAdminOnly(actor) && (
        <p className="text-xs text-[color:var(--ink-3)] -mt-3">
          {t(
            'admin.kuse_admin_scope_hint',
            'Du verwaltest nur dein Kundensegment ({{segments}}) — Benutzerrollen, Tags und Neue Nutzer.',
            { segments: (assignableSegments(actor) ?? []).join(', ') },
          )}
        </p>
      )}
      <nav className="flex gap-1 border-b border-[color:var(--line)] -mb-px">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            onClick={() => setTab(tb.key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
              tab === tb.key
                ? 'text-[color:var(--ink)]'
                : 'text-[color:var(--ink-3)] hover:text-[color:var(--ink)]'
            }`}
          >
            {tb.label}
            {tab === tb.key && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--primary)] rounded-full" />
            )}
          </button>
        ))}
      </nav>
      <div className="animate-fade-in">
        {tab === 'users' && <UserAdmin />}
        {tab === 'tags' && <TagsAdmin />}
        {tab === 'phases' && <PhaseAdmin />}
        {tab === 'salesforce' && <SalesforceAdmin />}
        {tab === 'flags' && <FeatureFlagsTab />}
        {tab === 'notifications' && <NotificationsAdmin />}
        {tab === 'actions' && <ActionTextsAdmin />}
        {tab === 'prompts' && <PromptsAdmin />}
        {tab === 'news' && <NewsAdmin />}
        {tab === 'neue_nutzer' && <NeueNutzerAdmin />}
      </div>
    </div>
  );
}

// F2: die frühere feste FN_ROLES-Liste lebt jetzt als `FULL_ADMIN_ASSIGNABLE_ROLES` in
// `admin-gating.ts` (Single Source of Truth für „welche Rollen darf DIESER Actor vergeben").

function UserAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const { user: currentUser } = useSession();
  const actor = currentUser ?? ADMIN_LOADING_FALLBACK_ACTOR;
  const usersQuery = useQuery({ queryKey: ['admin-users'], queryFn: api.adminListUsers });
  const tagsQuery = useQuery({ queryKey: ['tags-all'], queryFn: () => api.listTags() });
  const managedTagsByUser = useMemo(() => {
    const m = new Map<string, Tag[]>();
    for (const tag of tagsQuery.data?.items ?? []) {
      if (!tag.manager_user_id) continue;
      const arr = m.get(tag.manager_user_id) ?? [];
      arr.push(tag);
      m.set(tag.manager_user_id, arr);
    }
    return m;
  }, [tagsQuery.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    void queryClient.invalidateQueries({ queryKey: ['dev-users'] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['users-all'] });
  };

  const create = useMutation({
    mutationFn: api.adminCreateUser,
    onSuccess: () => {
      invalidate();
      toast.success(t('admin.user_created', 'User angelegt'));
    },
    onError: (err) =>
      toast.error(
        t('admin.user_create_failed', 'User konnte nicht angelegt werden'),
        (err as Error).message,
      ),
  });
  const remove = useMutation({
    mutationFn: api.adminDeleteUser,
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(t('admin.delete_failed', 'Löschen fehlgeschlagen'), (err as Error).message),
  });
  // Rollen vergeben/entziehen: dieselben Mutationen wie in „Neue Nutzer" (FunctionRoleEditor).
  const { assign, unassign } = useFunctionRoleMutations(invalidate);
  const setAdmin = useMutation({
    mutationFn: ({ userId, is_admin }: { userId: string; is_admin: boolean }) =>
      api.adminUpdateUser(userId, { is_admin }),
    onSuccess: (_data, { is_admin }) => {
      invalidate();
      toast.success(
        is_admin
          ? t('admin.admin_granted', 'Admin-Recht vergeben')
          : t('admin.admin_revoked', 'Admin-Recht entzogen'),
      );
    },
    onError: (err) =>
      toast.error(
        t('admin.admin_toggle_failed', 'Admin-Recht konnte nicht geändert werden'),
        (err as Error).message,
      ),
  });

  // #116: Aktivieren/Deaktivieren. Die Wirkung (Rollen weg, Admin-Recht weg bzw. „Neue Nutzer")
  // entscheidet der Server, deshalb Listen neu laden statt lokal zu schreiben.
  const setActive = useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      api.adminUpdateUser(userId, { active }),
    onSuccess: (_data, { active }) => {
      invalidate();
      toast.success(
        active
          ? t('admin.user_reaktiviert', 'Nutzer reaktiviert — er steht jetzt unter „Neue Nutzer"')
          : t('admin.user_deaktiviert', 'Nutzer deaktiviert'),
      );
    },
    onError: (err) =>
      toast.error(
        t('admin.user_status_failed', 'Status konnte nicht geändert werden'),
        (err as Error).message,
      ),
  });

  const [draft, setDraft] = useState({
    email: '',
    name: '',
  });
  // #116: Inaktive sind standardmäßig ausgeblendet (lokaler State, nicht gemerkt).
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter] = useState<'test' | 'real' | 'with_roles' | 'all'>('test');
  const [search, setSearch] = useState('');

  const sichtbar = useMemo(() => {
    const all = usersQuery.data?.items ?? [];
    return showInactive ? all : all.filter((u) => u.active !== false);
  }, [usersQuery.data, showInactive]);

  const filteredUsers = useMemo(() => {
    let list = sichtbar;
    if (filter === 'test') list = list.filter((u) => u.is_test_user);
    else if (filter === 'real') list = list.filter((u) => !u.is_test_user);
    else if (filter === 'with_roles') list = list.filter((u) => u.function_roles.length > 0);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sichtbar, filter, search]);

  const counts = useMemo(() => {
    const all = sichtbar;
    return {
      all: all.length,
      test: all.filter((u) => u.is_test_user).length,
      real: all.filter((u) => !u.is_test_user).length,
      with_roles: all.filter((u) => u.function_roles.length > 0).length,
    };
  }, [sichtbar]);

  const FILTER_TABS: { key: typeof filter; label: string; key_label: keyof typeof counts }[] = [
    { key: 'test', label: t('admin.filter_test_users', 'Test-User'), key_label: 'test' },
    { key: 'real', label: t('admin.filter_real_users', 'Echte User'), key_label: 'real' },
    {
      key: 'with_roles',
      label: t('admin.filter_with_roles', 'Mit Funktionsrolle'),
      key_label: 'with_roles',
    },
    { key: 'all', label: t('admin.filter_all_users', 'Alle'), key_label: 'all' },
  ];

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
            {t('admin.users_title', 'Benutzer & Funktionsrollen')}
          </h2>
          <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
            {t(
              'admin.users_subtitle',
              'Phase G Stub · Test-User für lokales Debuggen, echte User aus Salesforce-Sync',
            )}
          </p>
        </div>
        <div className="text-xs text-[color:var(--ink-3)]">
          {t('admin.users_total', '{{count}} User insgesamt', { count: counts.all })}
        </div>
      </div>

      {/* Filter-Tabs */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex p-1 rounded-xl bg-[var(--surface)] border border-[color:var(--line)]">
          {FILTER_TABS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filter === f.key
                  ? 'bg-[var(--primary)] text-[color:var(--on-primary)] shadow-sm'
                  : 'text-[color:var(--ink-2)] hover:text-[color:var(--ink)] hover:bg-[var(--surface-2)]'
              }`}
            >
              {f.label}
              <span
                className={`ml-1.5 text-[10px] ${
                  filter === f.key
                    ? 'text-[color:var(--on-primary)]/80'
                    : 'text-[color:var(--ink-4)]'
                }`}
              >
                {counts[f.key_label]}
              </span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {t('admin.user_inaktive_anzeigen', 'Inaktive anzeigen')}
        </label>
        <div className="relative ml-auto">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--ink-4)] pointer-events-none"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="9" cy="9" r="6" />
            <path d="M14 14l3 3" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.user_search_placeholder', 'Name, E-Mail…')}
            className="input pl-9 w-64"
          />
        </div>
      </div>

      {usersQuery.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}
      {usersQuery.data && (
        <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] text-left text-xs uppercase tracking-wide text-[color:var(--ink-3)] sticky top-0">
              <tr>
                <th className="px-3 py-2">{t('admin.col_name_email', 'Name / Email')}</th>
                <th className="px-3 py-2 w-24">{t('admin.col_system', 'System')}</th>
                <th className="px-3 py-2">{t('admin.col_function_roles', 'Funktionsrollen')}</th>
                <th className="px-3 py-2 w-20 text-right">{t('admin.col_action', 'Aktion')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--line-soft)]">
              {filteredUsers.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-sm text-[color:var(--ink-4)] italic"
                  >
                    {t('admin.users_filter_empty', 'Keine User in diesem Filter.')}
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => (
                <UserRow
                  key={u.user_id}
                  user={u}
                  actor={actor}
                  managedTags={managedTagsByUser.get(u.user_id) ?? []}
                  isSelf={u.user_id === currentUser?.user_id}
                  adminTogglePending={setAdmin.isPending}
                  onSetAdmin={(is_admin) => setAdmin.mutate({ userId: u.user_id, is_admin })}
                  statusPending={setActive.isPending}
                  onDeactivate={() => {
                    void confirmAction({
                      title: t('admin.user_deaktivieren_confirm_title', '{{name}} deaktivieren?', {
                        name: u.name,
                      }),
                      body: t(
                        'admin.user_deaktivieren_confirm_text',
                        '{{name}} wird deaktiviert: alle Rollen und das Admin-Recht werden entzogen, Anmelden ist nicht mehr möglich. Eingetragene Projektzuständigkeiten bleiben.',
                        { name: u.name },
                      ),
                      tone: 'danger',
                      confirmLabel: t('admin.user_deaktivieren', 'Deaktivieren'),
                    }).then((ok) => {
                      if (ok) setActive.mutate({ userId: u.user_id, active: false });
                    });
                  }}
                  onReactivate={() => setActive.mutate({ userId: u.user_id, active: true })}
                  onDelete={() => {
                    void confirmAction({
                      title: t('admin.user_delete_confirm', 'User „{{name}}" wirklich löschen?', {
                        name: u.name,
                      }),
                      tone: 'danger',
                      confirmLabel: t('common.delete', 'Löschen'),
                    }).then((ok) => {
                      if (ok) remove.mutate(u.user_id);
                    });
                  }}
                  onAssign={(role, scope_segment) =>
                    assign.mutate({ userId: u.user_id, role, scope_segment })
                  }
                  onUnassign={(rid) => unassign.mutate(rid)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* F2: Test-User anlegen läuft über POST /v1/admin/users — strikt is_admin-only im
          Backend (routes/admin.ts, requireAdmin()). Für KuSe-Admin ausgeblendet statt einen
          Klick anzubieten, der serverseitig sicher mit 403 scheitert. */}
      {canManageUserAccounts(actor) && (
        <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg p-3 space-y-2">
          <h3 className="text-sm font-medium">
            {t('admin.user_create_title', 'Neuen Test-User anlegen')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('admin.user_name_placeholder', 'Anzeigename')}
              className="input"
            />
            <input
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              placeholder={t('admin.user_email_placeholder', 'email@…local')}
              className="input"
            />
          </div>
          <p className="text-[11px] text-[color:var(--ink-4)] italic">
            {t(
              'admin.user_create_viewer_hint',
              'Neuer User startet als Betrachter (kein Admin, keine Funktionsrolle). Admin-Flag via API/DB setzen.',
            )}
          </p>
          <button
            type="button"
            onClick={() => {
              if (!draft.name.trim() || !draft.email.trim()) return;
              create.mutate({
                name: draft.name.trim(),
                email: draft.email.trim(),
                is_test_user: true,
              });
              setDraft({ email: '', name: '' });
            }}
            disabled={!draft.name.trim() || !draft.email.trim() || create.isPending}
            className="btn-primary"
          >
            {t('admin.user_create_btn', 'User anlegen')}
          </button>
        </div>
      )}
    </section>
  );
}

// Wandert mit der Rollen-Bedienung in den FunctionRoleEditor; hier weiter exportiert (admin.test.ts).
export { shouldShowNoRolesHint };

function UserRow({
  user,
  actor,
  managedTags,
  isSelf,
  adminTogglePending,
  onSetAdmin,
  statusPending,
  onDeactivate,
  onReactivate,
  onDelete,
  onAssign,
  onUnassign,
}: {
  user: AdminUser;
  /** Der eingeloggte User, der diese Zeile bedient (Gating-Basis, nicht `user` selbst!). */
  actor: PermissionUser;
  managedTags: Tag[];
  isSelf: boolean;
  adminTogglePending: boolean;
  onSetAdmin: (isAdmin: boolean) => void;
  statusPending: boolean;
  onDeactivate: () => void;
  onReactivate: () => void;
  onDelete: () => void;
  onAssign: (role: FunctionRole, scope: Segment | null) => void;
  onUnassign: (functionRoleId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const inaktiv = user.active === false;
  const canManageAccount = canManageUserAccounts(actor);

  return (
    <tr>
      <td className="px-3 py-2">
        <div className="font-medium text-[color:var(--ink)]">
          {user.name}
          {inaktiv && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--surface-3)] text-[color:var(--ink-3)] text-[10px] font-medium">
              {t('admin.user_inaktiv_badge', 'inaktiv seit {{datum}}', {
                datum: user.deaktiviert_am
                  ? formatZeitpunkt(user.deaktiviert_am, i18n.language, 'datum')
                  : '–',
              })}
            </span>
          )}
        </div>
        <div className="text-xs text-[color:var(--ink-3)]">
          {user.email}
          {user.is_test_user && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--secondary-soft)] text-[color:var(--primary-strong)] text-[10px] font-medium">
              TEST
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        {/* is_admin als Admin-Toggle. Self-Demotion ist deaktiviert (Backend lehnt sie mit
            400 ab) — verhindert versehentliche Selbst-Aussperrung. F2: der Toggle bedient
            PATCH /v1/admin/users/:id (strikt is_admin-only im Backend) — für KuSe-Admin
            (canManageAccount=false) nur die reine Anzeige, kein interaktiver Schalter. */}
        {canManageAccount ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={user.is_admin}
              aria-label={t('admin.is_admin_toggle_label', 'Admin-Recht für {{name}}', {
                name: user.name,
              })}
              disabled={isSelf || adminTogglePending || inaktiv}
              onClick={() => onSetAdmin(!user.is_admin)}
              title={
                isSelf
                  ? t(
                      'admin.is_admin_self_hint',
                      'Das eigene Admin-Recht kann nicht entzogen werden',
                    )
                  : user.is_admin
                    ? t('admin.is_admin_yes_hint', 'Administrator')
                    : t('admin.is_admin_no_hint', 'Kein Admin — Rechte via Funktionsrollen')
              }
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                user.is_admin ? 'bg-[var(--primary)]' : 'bg-[var(--surface-3)]'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  user.is_admin ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-[11px] text-[color:var(--ink-3)]">
              {user.is_admin ? t('common.admin') : t('common.viewer')}
            </span>
          </div>
        ) : (
          <span className="text-[11px] text-[color:var(--ink-3)]">
            {user.is_admin ? t('common.admin') : t('common.viewer')}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <FunctionRoleEditor
          user={user}
          actor={actor}
          isSelf={isSelf}
          inaktiv={inaktiv}
          onAssign={onAssign}
          onUnassign={onUnassign}
          extra={
            managedTags.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 mb-1.5">
                <span className="text-[10px] uppercase tracking-wide text-[color:var(--ink-4)]">
                  {t('admin.user_manages', 'Manager:')}
                </span>
                {managedTags.map((tag) => (
                  <span
                    key={tag.tag_id}
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: `${tag.color ?? '#64748B'}20`,
                      color: tag.color ?? '#64748B',
                      border: `1px solid ${tag.color ?? '#64748B'}40`,
                    }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            ) : null
          }
        />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {/* #116: Deaktivieren/Reaktivieren wie die Löschung nur für Voll-Admins; bei der eigenen
            Zeile gar nicht angeboten (das Backend lehnt es mit 400 self_deactivate ab). */}
        {canManageAccount &&
          !isSelf &&
          (inaktiv ? (
            <button
              type="button"
              onClick={onReactivate}
              disabled={statusPending}
              className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--primary)] px-2 py-1 rounded disabled:opacity-50"
            >
              {t('admin.user_reaktivieren', 'Reaktivieren')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onDeactivate}
              disabled={statusPending}
              className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--danger)] px-2 py-1 rounded disabled:opacity-50"
            >
              {t('admin.user_deaktivieren', 'Deaktivieren')}
            </button>
          ))}
        {/* F2: User-Löschung läuft über DELETE /v1/admin/users/:id — strikt is_admin-only
            im Backend, siehe canManageUserAccounts-Doku. */}
        {canManageAccount && (
          <button
            type="button"
            onClick={onDelete}
            className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--danger)] px-2 py-1 rounded"
            title={
              user.is_test_user
                ? t('admin.delete_test_user', 'Test-User löschen')
                : t('admin.delete_user', 'User löschen')
            }
          >
            {t('admin.delete_short', 'löschen')}
          </button>
        )}
      </td>
    </tr>
  );
}

// --- Tags / Custom Labels Admin ----------------------------------------------

const TAG_COLOR_PRESETS = [
  '#EA5B0B',
  '#FDC300',
  '#0EA5E9',
  '#10B981',
  '#8B5CF6',
  '#EF4444',
  '#64748B',
  '#EC4899',
  '#84CC16',
  '#6366F1',
];

function TagsAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const { user: sessionUser } = useSession();
  const actor = sessionUser ?? ADMIN_LOADING_FALLBACK_ACTOR;
  const tagsQuery = useQuery({ queryKey: ['tags-all'], queryFn: () => api.listTags() });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => api.listUsers() });
  const users = usersQuery.data?.items ?? [];
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.name);
    return m;
  }, [users]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tags-all'] });
    void queryClient.invalidateQueries({ queryKey: ['tags-by-segment'] });
    void queryClient.invalidateQueries({ queryKey: ['project-tags'] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const create = useMutation({
    mutationFn: api.createTag,
    onSuccess: () => {
      invalidate();
      toast.success(t('admin.tag_created', 'Tag angelegt'));
    },
    onError: (err) =>
      toast.error(
        t('admin.tag_create_failed', 'Tag konnte nicht angelegt werden'),
        (err as Error).message,
      ),
  });
  const update = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Parameters<typeof api.updateTag>[1]) =>
      api.updateTag(id, data),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        t('admin.tag_update_failed', 'Tag-Update fehlgeschlagen'),
        (err as Error).message,
      ),
  });
  const remove = useMutation({
    mutationFn: api.deleteTag,
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        t('admin.tag_delete_failed', 'Tag-Löschen fehlgeschlagen'),
        (err as Error).message,
      ),
  });

  const [scope, setScope] = useState<Segment | 'global'>('KI');
  const [draft, setDraft] = useState<{
    name: string;
    color: string;
    description: string;
    manager_user_id: string;
  }>({
    name: '',
    color: TAG_COLOR_PRESETS[0] ?? '#EA5B0B',
    description: '',
    manager_user_id: '',
  });

  const tagsByScope = useMemo(() => {
    const m = new Map<string, Tag[]>();
    for (const tag of tagsQuery.data?.items ?? []) {
      const k = tag.scope_segment ?? 'global';
      const arr = m.get(k) ?? [];
      arr.push(tag);
      m.set(k, arr);
    }
    return m;
  }, [tagsQuery.data]);

  const SCOPES: { key: Segment | 'global'; label: string }[] = [
    { key: 'KI', label: 'KI' },
    { key: 'IT', label: 'IT' },
    { key: 'ProLo', label: 'ProLo' },
    { key: 'V&S', label: 'V&S' },
    { key: 'HR', label: 'HR' },
    { key: 'global', label: t('admin.scope_global_label', 'Global') },
  ];

  const currentTags = tagsByScope.get(scope) ?? [];
  // F2: dieselbe shared Prädikat-Funktion wie im Backend (`routes/tags.ts`) entscheidet, ob
  // im aktuell gewählten Tab überhaupt angelegt werden darf — deckt is_admin, bereichsleiter
  // UND kuse_admin ab, ohne dass hier Rollenlogik nachgebaut wird.
  const canManageCurrentScope = canManageTagInScope(actor, scope === 'global' ? null : scope);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.tags_title', 'Tags / Custom Labels')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.tags_subtitle',
            'Pro Bereich pflegbar. Bereichsleitung pflegt eigenen Bereich, Admin pflegt alle inkl. globale Tags. Ein Tag ist nur an Projekten des gleichen Segments anwendbar (globale Tags überall).',
          )}
        </p>
      </div>

      <div className="inline-flex p-1 rounded-xl bg-[var(--surface)] border border-[color:var(--line)]">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setScope(s.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              scope === s.key
                ? 'bg-[var(--primary)] text-[color:var(--on-primary)] shadow-sm'
                : 'text-[color:var(--ink-2)] hover:text-[color:var(--ink)] hover:bg-[var(--surface-2)]'
            }`}
          >
            {s.label}
            <span
              className={`ml-1.5 text-[10px] ${
                scope === s.key ? 'text-[color:var(--on-primary)]/80' : 'text-[color:var(--ink-4)]'
              }`}
            >
              {tagsByScope.get(s.key)?.length ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)] text-left text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
            <tr>
              <th className="px-3 py-2 w-12">{t('admin.col_color', 'Farbe')}</th>
              <th className="px-3 py-2">{t('admin.col_name', 'Name')}</th>
              <th className="px-3 py-2">{t('admin.col_description', 'Beschreibung')}</th>
              <th className="px-3 py-2 w-48">{t('admin.col_manager', 'Manager')}</th>
              <th className="px-3 py-2 w-24 text-right">{t('admin.col_action', 'Aktion')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--line-soft)]">
            {currentTags.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-sm text-[color:var(--ink-4)] italic"
                >
                  {t('admin.tags_scope_empty', 'Noch keine Tags in diesem Bereich.')}
                </td>
              </tr>
            )}
            {currentTags.map((tag) => (
              <TagRow
                key={tag.tag_id}
                tag={tag}
                users={users}
                userNameById={userNameById}
                canManage={canManageTagInScope(actor, tag.scope_segment ?? null)}
                onUpdate={(data) => update.mutate({ id: tag.tag_id, ...data })}
                onDelete={() => {
                  void confirmAction({
                    title: t('admin.tag_delete_confirm', 'Tag „{{name}}" wirklich löschen?', {
                      name: tag.name,
                    }),
                    tone: 'danger',
                    confirmLabel: t('common.delete', 'Löschen'),
                  }).then((ok) => {
                    if (ok) remove.mutate(tag.tag_id);
                  });
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* F2: Anlegen nur mit scope_segment aus den eigenen kuse_admin-Segmenten (Task-Brief
          §4) — canManageTagInScope ist dieselbe Prädikat-Funktion, die der Backend-Guard in
          routes/tags.ts prüft, hier nur zusätzlich fürs Ausblenden verwendet. Globale Tags
          (scope='global') bleiben damit für kuse_admin automatisch read-only, weil
          canManageTagInScope(actor, null) für nicht-is_admin immer false liefert. */}
      {canManageCurrentScope && (
        <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg p-3 space-y-2">
          <h3 className="text-sm font-medium">
            {t('admin.tag_create_title', 'Neues Tag in „{{scope}}"', {
              scope: scope === 'global' ? t('admin.scope_global_label', 'Global') : scope,
            })}
          </h3>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[color:var(--ink-3)] block mb-1">
                {t('admin.col_color', 'Farbe')}
              </label>
              <div className="flex gap-1">
                {TAG_COLOR_PRESETS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setDraft({ ...draft, color: c })}
                    style={{ backgroundColor: c }}
                    className={`w-6 h-6 rounded-full border-2 ${
                      draft.color === c
                        ? 'border-[color:var(--ink)] scale-110'
                        : 'border-[color:var(--surface)]'
                    } shadow-sm transition-transform`}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('admin.tag_name_placeholder', 'Tag-Name (z. B. "Strategisch")')}
              className="input flex-1 min-w-[180px]"
              maxLength={64}
            />
            <input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder={t('admin.tag_desc_placeholder', 'Beschreibung (optional)')}
              className="input flex-[2] min-w-[200px]"
              maxLength={500}
            />
            <label className="min-w-[180px]">
              <span className="text-[11px] uppercase tracking-wide text-[color:var(--ink-3)] block mb-1">
                {t('admin.col_manager', 'Manager')}
              </span>
              <select
                value={draft.manager_user_id}
                onChange={(e) => setDraft({ ...draft, manager_user_id: e.target.value })}
                className="input"
                title={t(
                  'admin.manager_hint',
                  'Schaltet diesem User die Manager-Sicht auf Projekte dieses Themenbereichs frei.',
                )}
              >
                <option value="">{t('admin.manager_none', '— kein Manager —')}</option>
                {users
                  .filter((u) => u.active !== false || u.user_id === draft.manager_user_id)
                  .map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {anzeigeName(u, t)}
                    </option>
                  ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => {
                if (!draft.name.trim()) return;
                create.mutate({
                  name: draft.name.trim(),
                  color: draft.color,
                  scope_segment: scope === 'global' ? null : scope,
                  description: draft.description.trim() || null,
                  manager_user_id: draft.manager_user_id || null,
                });
                setDraft({ ...draft, name: '', description: '', manager_user_id: '' });
              }}
              disabled={!draft.name.trim() || create.isPending}
              className="btn-primary"
            >
              {t('admin.tag_create_btn', 'Anlegen')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function TagRow({
  tag,
  users,
  userNameById,
  canManage,
  onUpdate,
  onDelete,
}: {
  tag: Tag;
  users: User[];
  userNameById: Map<string, string>;
  /** F2: `canManageTagInScope(actor, tag.scope_segment)` — globale Tags sind für
      kuse_admin damit automatisch read-only (keine Bearbeiten-/Löschen-Aktionen). */
  canManage: boolean;
  onUpdate: (data: Parameters<typeof api.updateTag>[1]) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: tag.name,
    color: tag.color ?? '#EA5B0B',
    description: tag.description ?? '',
    manager_user_id: tag.manager_user_id ?? '',
  });
  const managerName = tag.manager_user_id ? userNameById.get(tag.manager_user_id) : undefined;

  if (!editing) {
    return (
      <tr>
        <td className="px-3 py-2">
          <span
            className="inline-block w-5 h-5 rounded-full border border-[color:var(--line)]"
            style={{ backgroundColor: tag.color ?? '#999' }}
          />
        </td>
        <td className="px-3 py-2">
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
            style={{
              backgroundColor: `${tag.color ?? '#64748B'}20`,
              color: tag.color ?? '#64748B',
              border: `1px solid ${tag.color ?? '#64748B'}40`,
            }}
          >
            {tag.name}
          </span>
        </td>
        <td className="px-3 py-2 text-[color:var(--ink-2)] text-xs">{tag.description ?? ''}</td>
        <td className="px-3 py-2 text-xs">
          {managerName ? (
            <span className="text-[color:var(--ink)]">{managerName}</span>
          ) : (
            <span className="text-[color:var(--ink-4)] italic">
              {t('admin.manager_none', '— kein Manager —')}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right space-x-1">
          {canManage ? (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--ink)] px-2 py-1 rounded"
              >
                {t('common.edit', 'Bearbeiten')}
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--danger)] px-2 py-1 rounded"
              >
                ✕
              </button>
            </>
          ) : (
            <span className="text-[11px] text-[color:var(--ink-4)] italic">
              {t('admin.tag_read_only', 'nur lesbar')}
            </span>
          )}
        </td>
      </tr>
    );
  }
  return (
    <tr className="bg-[var(--surface-2)]/50">
      <td className="px-3 py-2">
        <div className="flex gap-1 flex-wrap">
          {TAG_COLOR_PRESETS.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => setDraft({ ...draft, color: c })}
              style={{ backgroundColor: c }}
              className={`w-5 h-5 rounded-full border-2 ${
                draft.color === c ? 'border-[color:var(--ink)]' : 'border-[color:var(--surface)]'
              }`}
              aria-label={c}
            />
          ))}
        </div>
      </td>
      <td className="px-3 py-2">
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="input text-sm py-1"
          maxLength={64}
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          className="input text-sm py-1"
          maxLength={500}
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={draft.manager_user_id}
          onChange={(e) => setDraft({ ...draft, manager_user_id: e.target.value })}
          className="input text-sm py-1"
        >
          <option value="">{t('admin.manager_none', '— kein Manager —')}</option>
          {users
            .filter((u) => u.active !== false || u.user_id === draft.manager_user_id)
            .map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {anzeigeName(u, t)}
              </option>
            ))}
        </select>
      </td>
      <td className="px-3 py-2 text-right space-x-1">
        <button
          type="button"
          onClick={() => {
            onUpdate({
              name: draft.name,
              color: draft.color,
              description: draft.description || null,
              manager_user_id: draft.manager_user_id || null,
            });
            setEditing(false);
          }}
          className="text-xs px-2 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)]"
        >
          {t('common.save', 'Speichern')}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-xs px-2 py-1 rounded border border-[color:var(--line)]"
        >
          ✕
        </button>
      </td>
    </tr>
  );
}

function SalesforceAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [sinceDate, setSinceDate] = useState<string>(today);
  // Manuellen Lauf bei Erfolg als neues Delta-Wasserzeichen übernehmen (Default an).
  const [setWatermarkOnSync, setSetWatermarkOnSync] = useState<boolean>(true);
  // Segment-Override für den manuellen Trigger. null = Fallback auf gespeicherte Whitelist.
  const [triggerSegments, setTriggerSegments] = useState<Segment[] | null>(null);

  const testQuery = useQuery({
    queryKey: ['sf-test'],
    queryFn: api.testSalesforce,
    refetchOnMount: 'always',
  });
  const runsQuery = useQuery({ queryKey: ['sync-runs'], queryFn: api.listSyncRuns });
  const scheduleQuery = useQuery({
    queryKey: ['sync-schedule'],
    queryFn: api.getSyncSchedule,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
  const describeQuery = useQuery({
    queryKey: ['sf-describe'],
    queryFn: () => api.describeSfObject(),
    enabled: testQuery.data?.ok === true,
  });

  // Vorbelegung des Trigger-Segment-Overrides mit der gespeicherten Whitelist.
  useEffect(() => {
    if (scheduleQuery.data) {
      setTriggerSegments(scheduleQuery.data.segment_whitelist ?? null);
    }
  }, [scheduleQuery.data]);

  const triggerSync = useMutation({
    mutationFn: () =>
      api.triggerSync({
        ...(sinceDate ? { since_date: sinceDate } : {}),
        set_watermark: setWatermarkOnSync,
        // Nur senden wenn nicht null (null = Fallback auf DB-Whitelist).
        ...(triggerSegments !== null && triggerSegments.length > 0
          ? { segments: triggerSegments }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sync-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['sync-schedule'] });
    },
  });

  return (
    <section className="space-y-3">
      <h2 className="font-medium">{t('admin.sync')}</h2>

      <div
        className="bg-[var(--surface)] rounded-xl border p-4 space-y-3"
        style={{ borderColor: 'var(--line-soft)', boxShadow: 'var(--shadow-card)' }}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-sm">
            {t('admin.sf_connection', 'Salesforce-Verbindung:')}
          </span>
          {testQuery.isLoading && <span className="text-sm">…</span>}
          {testQuery.data?.ok === true && (
            <span className="text-xs px-2 py-0.5 rounded bg-[var(--success-soft)] text-[color:var(--success)]">
              {t('admin.sf_connected', 'verbunden')}
            </span>
          )}
          {testQuery.data?.ok === false && (
            <span
              className="text-xs px-2 py-0.5 rounded bg-[var(--danger-soft)] text-[color:var(--danger)]"
              title={testQuery.data.reason}
            >
              {t('admin.sf_failed', 'fehlgeschlagen: {{reason}}', {
                reason: testQuery.data.reason,
              })}
            </span>
          )}
          <button
            type="button"
            onClick={() => testQuery.refetch()}
            className="ml-auto text-xs px-2 py-1 rounded border"
          >
            {t('admin.sf_recheck', 'erneut prüfen')}
          </button>
        </div>

        {testQuery.data?.ok === true && (
          <div className="text-xs text-[color:var(--ink-2)] space-y-0.5">
            <div>
              {t('admin.sf_instance', 'Instance:')} <code>{testQuery.data.instanceUrl}</code>
            </div>
            <div>
              {t('admin.sf_api', 'API:')} v{testQuery.data.apiVersion} ·{' '}
              {t('admin.sf_object', 'Objekt:')} <code>{testQuery.data.projectObject}</code>
            </div>
            <details>
              <summary className="cursor-pointer">
                {t('admin.sf_field_mapping', 'Field-Mapping')}
              </summary>
              <pre className="text-[11px] bg-[var(--surface-2)] p-2 rounded mt-1">
                {JSON.stringify(testQuery.data.fieldMap, null, 2)}
              </pre>
            </details>
            {describeQuery.data && (
              <details>
                <summary className="cursor-pointer">
                  {t('admin.sf_fields_of', 'Felder von {{name}} ({{count}})', {
                    name: describeQuery.data.name,
                    count: describeQuery.data.fields.length,
                  })}
                </summary>
                <ul className="text-[11px] bg-[var(--surface-2)] p-2 rounded mt-1 max-h-48 overflow-y-auto">
                  {describeQuery.data.fields.map((f) => (
                    <li key={f.name}>
                      <code>{f.name}</code>{' '}
                      <span className="text-[color:var(--ink-3)]">({f.type})</span> — {f.label}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div
        className="bg-[var(--surface)] rounded-xl border p-4 space-y-3"
        style={{ borderColor: 'var(--line-soft)', boxShadow: 'var(--shadow-card)' }}
      >
        <h3 className="text-sm font-medium">
          {t('admin.sync_start_title', 'Sync starten (Demo: nach Erstelldatum)')}
        </h3>
        <div className="flex items-end gap-2">
          <label className="text-xs text-[color:var(--ink-2)]">
            {t('admin.sync_since_date', 'Erstelldatum ab')}
            <input
              type="date"
              value={sinceDate}
              onChange={(e) => setSinceDate(e.target.value)}
              className="block mt-0.5 border rounded px-2 py-1 text-sm"
              style={{
                borderColor: 'var(--line-soft)',
                background: 'var(--surface-2)',
                color: 'var(--ink)',
              }}
            />
          </label>
          <button
            type="button"
            onClick={() => triggerSync.mutate()}
            disabled={triggerSync.isPending || testQuery.data?.ok !== true}
            className="px-3 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] text-sm disabled:opacity-50"
          >
            {triggerSync.isPending
              ? t('admin.sync_running', 'läuft…')
              : t('admin.sync_start_btn', 'Sync starten')}
          </button>
        </div>
        <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-2)' }}>
          <input
            type="checkbox"
            checked={setWatermarkOnSync}
            onChange={(e) => setSetWatermarkOnSync(e.target.checked)}
          />
          {t('admin.sync_set_watermark', 'Diesen Lauf als neues Delta-Wasserzeichen übernehmen')}
        </label>

        {/* Segment-Override für diesen manuellen Lauf */}
        <SegmentWhitelistEditor
          value={triggerSegments}
          onChange={setTriggerSegments}
          title={t('admin.sync_trigger_seg_title', 'Segment-Filter für diesen Lauf')}
          hint={t(
            'admin.sync_trigger_seg_hint',
            'Leer = alle Segmente (oder gespeicherte Worker-Whitelist). Vorbelegt mit aktiver Whitelist des Schedules.',
          )}
        />

        {triggerSync.data && (
          <div className="text-xs bg-[var(--surface-2)] rounded p-2 space-y-1">
            <div>
              {t('admin.sync_status_label', 'Status:')} <strong>{triggerSync.data.status}</strong> ·
              {t('admin.sync_counts_fetched', 'abgerufen')}: {triggerSync.data.counts.fetched} ·{' '}
              {t('admin.sync_counts_created', 'angelegt')}: {triggerSync.data.counts.created} ·{' '}
              {t('admin.sync_counts_updated', 'aktualisiert')}: {triggerSync.data.counts.updated} ·{' '}
              {t('admin.sync_counts_skipped_seg', 'übersprungen (Segment)')}:{' '}
              {triggerSync.data.counts.skipped_segment} ·{' '}
              {t('admin.sync_counts_skipped_no_seg', 'übersprungen (kein Segment)')}:{' '}
              {triggerSync.data.counts.skipped_no_segment} ·{' '}
              {t('admin.sync_counts_errors', 'Fehler')}: {triggerSync.data.counts.errors}
            </div>
            <IstFehlerHinweis anzahl={triggerSync.data.counts.ist_fehler} />
            <SfFehlendHinweis counts={triggerSync.data.counts} />
            {triggerSync.data.errors.length > 0 && (
              <details>
                <summary className="cursor-pointer text-[color:var(--danger)]">
                  {t('admin.sync_errors_count', '{{count}} Fehler', {
                    count: triggerSync.data.errors.length,
                  })}
                </summary>
                <ul className="mt-1 list-disc pl-4">
                  {triggerSync.data.errors.slice(0, 10).map((e, i) => (
                    <li key={`${e.salesforce_object_id}-${i}`}>
                      <code>{e.salesforce_object_id || '–'}</code>: {e.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {triggerSync.isError && (
          <p className="text-xs text-[color:var(--danger)]">
            {(triggerSync.error as Error).message}
          </p>
        )}
      </div>

      <SyncScheduleCard />

      <div
        className="bg-[var(--surface)] rounded-xl border"
        style={{ borderColor: 'var(--line-soft)', boxShadow: 'var(--shadow-card)' }}
      >
        <div className="px-3 py-2 border-b text-sm font-medium">
          {t('admin.sync_runs_title', 'Letzte Sync-Läufe')}
        </div>
        {runsQuery.data && runsQuery.data.items.length === 0 && (
          <p className="text-sm text-[color:var(--ink-3)] px-3 py-2">
            {t('admin.sync_runs_empty', 'Noch keine Sync-Läufe.')}
          </p>
        )}
        <ul className="text-xs">
          {runsQuery.data?.items.slice(0, 10).map((r) => (
            <li key={r.sync_run_id} className="px-3 py-2 border-b last:border-b-0">
              <div className="flex items-center gap-2">
                <span className="text-[color:var(--ink-3)]">
                  {new Date(r.start_at).toLocaleString()}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded uppercase text-[10px] ${
                    r.status === 'success'
                      ? 'bg-[var(--success-soft)] text-[color:var(--success)]'
                      : r.status === 'partial'
                        ? 'bg-[var(--warn-soft)] text-[color:var(--warn)]'
                        : r.status === 'failure'
                          ? 'bg-[var(--danger-soft)] text-[color:var(--danger)]'
                          : 'bg-[var(--surface-3)] text-[color:var(--ink-2)]'
                  }`}
                >
                  {r.status}
                </span>
                <span className="text-[color:var(--ink-3)]">{r.triggered_by}</span>
                {r.alarm_triggered && (
                  <span className="text-[color:var(--danger)] text-[10px]">
                    {t('admin.sync_alarm', '⚠ Alarm')}
                  </span>
                )}
              </div>
              {r.counts && (
                <div className="text-[color:var(--ink-3)] mt-0.5">
                  {t('admin.sync_counts_fetched', 'abgerufen')}:{' '}
                  {(r.counts as Record<string, number>).fetched} ·{' '}
                  {t('admin.sync_counts_created', 'angelegt')}:{' '}
                  {(r.counts as Record<string, number>).created} ·{' '}
                  {t('admin.sync_counts_updated', 'aktualisiert')}:{' '}
                  {(r.counts as Record<string, number>).updated} ·{' '}
                  {t('admin.sync_counts_skipped_seg', 'übersprungen (Segment)')}:{' '}
                  {(r.counts as Record<string, number>).skipped_segment ?? 0} ·{' '}
                  {t('admin.sync_counts_skipped_no_seg', 'übersprungen (kein Segment)')}:{' '}
                  {(r.counts as Record<string, number>).skipped_no_segment ?? 0} ·{' '}
                  {t('admin.sync_counts_errors', 'Fehler')}:{' '}
                  {(r.counts as Record<string, number>).errors}
                </div>
              )}
              {r.counts && (
                <IstFehlerHinweis anzahl={(r.counts as Record<string, number>).ist_fehler} />
              )}
              {r.counts && <SfFehlendHinweis counts={r.counts as Record<string, number>} />}
              {r.error_summary && (
                <div className="text-[color:var(--danger)] mt-0.5 truncate" title={r.error_summary}>
                  {r.error_summary}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * PV-052 L3: fehlgeschlagene Ist-Abfragen eines Sync-Laufs. Der Lauf bleibt `success` (das Ist ist
 * Zusatzinformation), die Fehler sollen trotzdem nicht nur im Log stehen. Nichts bei 0/fehlend.
 */
export function IstFehlerHinweis({ anzahl }: { anzahl: number | undefined }) {
  const { t } = useTranslation();
  if (!anzahl) return null;
  return (
    <div className="text-[color:var(--warn)] mt-0.5">
      {t('admin.sync_counts_ist_fehler', 'Ist-Abfragen fehlgeschlagen: {{count}}', {
        count: anzahl,
      })}
    </div>
  );
}

/**
 * #131: Keys, die der Refresh in Salesforce nicht fand (nur Refresh-Läufe zählen das). Gelöschte
 * Aufträge hat die PV automatisch abgeschlossen; nicht auffindbare/umbenannte nur gemeldet. Der
 * Lauf bleibt `success` — die Zahlen sollen trotzdem nicht nur im Log stehen. Nichts bei 0/fehlend.
 */
export function SfFehlendHinweis({
  counts,
}: {
  counts: {
    sf_geloescht?: number | undefined;
    sf_nicht_auffindbar?: number | undefined;
    sf_vorhanden_anderer_key?: number | undefined;
    sf_pruef_fehler?: number | undefined;
  };
}) {
  const { t } = useTranslation();
  const geloescht = counts.sf_geloescht ?? 0;
  const nichtAuffindbar = counts.sf_nicht_auffindbar ?? 0;
  const andererKey = counts.sf_vorhanden_anderer_key ?? 0;
  const zeigeZahlen = geloescht + nichtAuffindbar + andererKey > 0;
  if (!zeigeZahlen && !counts.sf_pruef_fehler) return null;
  return (
    <>
      {zeigeZahlen && (
        <div className="text-[color:var(--warn)] mt-0.5">
          {t(
            'admin.sync_counts_sf_fehlend',
            'In Salesforce fehlend: {{geloescht}} gelöscht (abgeschlossen) · {{nicht_auffindbar}} nicht auffindbar · {{anderer_key}} unter anderer P-Nummer',
            { geloescht, nicht_auffindbar: nichtAuffindbar, anderer_key: andererKey },
          )}
        </div>
      )}
      {!!counts.sf_pruef_fehler && (
        <div className="text-[color:var(--warn)] mt-0.5">
          {t(
            'admin.sync_counts_sf_pruef_fehler',
            'Prüfung fehlender Aufträge in Salesforce fehlgeschlagen',
          )}
        </div>
      )}
    </>
  );
}

/** 401 loest den globalen Login-Redirect in `request()` aus — das ist kein Fehler zum Anzeigen. */
function isUnauthorized(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    (err as { status: unknown }).status === 401
  );
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

const DUE_MEANINGS: PhaseDueMeaning[] = ['none', 'erstgespraech', 'kickoff', 'fertigstellung'];

function DueMeaningSelect({
  value,
  onChange,
  className,
  id,
}: {
  value: PhaseDueMeaning;
  onChange: (v: PhaseDueMeaning) => void;
  className?: string;
  id?: string;
}) {
  const { t } = useTranslation();
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as PhaseDueMeaning)}
      className={className ?? 'border rounded px-2 py-1 text-sm'}
      aria-label={t('admin.col_due_meaning', 'Datum bedeutet')}
    >
      {DUE_MEANINGS.map((m) => (
        <option key={m} value={m}>
          {t(`admin.due_meaning_${m}`)}
        </option>
      ))}
    </select>
  );
}

function PhaseAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const phasesQuery = useQuery({ queryKey: ['phases'], queryFn: api.listPhases });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['phases'] });

  // Nicht-401-Fehler sichtbar machen; 401 löst den globalen Login-Redirect in
  // request() aus (abgelaufene Session) — dann keine zusätzliche Meldung.
  const onSaveError = (err: unknown) => {
    if (isUnauthorized(err)) return;
    toast.error(
      t('admin.phase_save_failed_title', 'Phase konnte nicht gespeichert werden'),
      errorMessage(err),
    );
  };

  const create = useMutation({
    mutationFn: api.createPhase,
    onSuccess: invalidate,
    onError: onSaveError,
  });
  const update = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      order?: number;
      description?: string;
      due_meaning?: PhaseDueMeaning;
    }) => api.updatePhase(id, data),
    onSuccess: invalidate,
    onError: onSaveError,
  });
  const remove = useMutation({
    mutationFn: api.deletePhase,
    onSuccess: invalidate,
    onError: (err) => {
      toast.error(
        t('admin.phase_delete_failed_title', 'Phase konnte nicht gelöscht werden'),
        errorMessage(err),
      );
    },
  });

  const [draft, setDraft] = useState<{
    name: string;
    order: number;
    description: string;
    due_meaning: PhaseDueMeaning;
  }>({ name: '', order: 0, description: '', due_meaning: 'none' });

  const onCreate = () => {
    if (!draft.name.trim()) return;
    const description = draft.description.trim();
    create.mutate({
      name: draft.name.trim(),
      order: draft.order,
      due_meaning: draft.due_meaning,
      ...(description ? { description } : {}),
    });
    setDraft({ name: '', order: 0, description: '', due_meaning: 'none' });
  };

  return (
    <section className="space-y-3">
      <h2 className="font-medium">{t('admin.phases')}</h2>

      {phasesQuery.isLoading && <p>{t('common.loading')}</p>}
      {phasesQuery.data && (
        <div className="bg-[var(--surface)] border rounded">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] text-left text-xs uppercase text-[color:var(--ink-2)]">
              <tr>
                <th className="px-3 py-2 w-20">{t('admin.col_order')}</th>
                <th className="px-3 py-2">{t('admin.col_name')}</th>
                <th className="px-3 py-2">{t('admin.col_description')}</th>
                <th className="px-3 py-2 w-44">{t('admin.col_due_meaning', 'Datum bedeutet')}</th>
                <th className="px-3 py-2 w-32 text-right">{t('admin.col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {phasesQuery.data.items
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((p) => (
                  <PhaseRow
                    key={p.phase_id}
                    phase={p}
                    onUpdate={(data) => update.mutate({ id: p.phase_id, ...data })}
                    onDelete={() => {
                      void confirmAction({
                        title: t('admin.phase_delete_title', 'Phase löschen?'),
                        body: t('admin.confirm_delete', { name: p.name }),
                        tone: 'danger',
                        confirmLabel: t('common.delete', 'Löschen'),
                      }).then((ok) => {
                        if (ok) remove.mutate(p.phase_id);
                      });
                    }}
                  />
                ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-[var(--surface)] border rounded p-3 space-y-2">
        <h3 className="text-sm font-medium">{t('admin.add_phase')}</h3>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-[color:var(--ink-2)]">
            {t('admin.col_order')}
            <input
              type="number"
              value={draft.order}
              onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
              className="block mt-0.5 border rounded px-2 py-1 w-20 text-sm"
            />
          </label>
          <label className="text-xs text-[color:var(--ink-2)] flex-1 min-w-[160px]">
            {t('admin.col_name')}
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="block mt-0.5 border rounded px-2 py-1 w-full text-sm"
            />
          </label>
          <label className="text-xs text-[color:var(--ink-2)] flex-1 min-w-[200px]">
            {t('admin.col_description')}
            <input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="block mt-0.5 border rounded px-2 py-1 w-full text-sm"
            />
          </label>
          <label className="text-xs text-[color:var(--ink-2)]" htmlFor="phase-due-meaning-create">
            {t('admin.col_due_meaning', 'Datum bedeutet')}
            <DueMeaningSelect
              id="phase-due-meaning-create"
              value={draft.due_meaning}
              onChange={(due_meaning) => setDraft({ ...draft, due_meaning })}
              className="block mt-0.5 border rounded px-2 py-1 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={onCreate}
            disabled={!draft.name.trim() || create.isPending}
            className="px-3 py-1 rounded bg-[var(--ink)] text-[color:var(--on-primary)] text-sm disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </section>
  );
}

/** Exportiert fuer Tests (admin-phase-row.test.tsx) -- sonst nur intern in PhaseAdmin genutzt. */
export function PhaseRow({
  phase,
  onUpdate,
  onDelete,
}: {
  phase: Phase;
  onUpdate: (data: {
    name?: string;
    order?: number;
    description?: string;
    due_meaning?: PhaseDueMeaning;
  }) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{
    name: string;
    order: number;
    description: string;
    due_meaning: PhaseDueMeaning;
  }>({
    name: phase.name,
    order: phase.order,
    description: phase.description ?? '',
    due_meaning: phase.due_meaning ?? 'none',
  });

  if (!editing) {
    return (
      <tr className="border-t">
        <td className="px-3 py-2 text-[color:var(--ink-3)]">{phase.order}</td>
        <td className="px-3 py-2 font-medium">{phase.name}</td>
        <td className="px-3 py-2 text-[color:var(--ink-2)]">{phase.description ?? ''}</td>
        <td className="px-3 py-2 text-[color:var(--ink-2)]">
          {t(`admin.due_meaning_${phase.due_meaning ?? 'none'}`)}
        </td>
        <td className="px-3 py-2 text-right space-x-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs px-2 py-1 rounded border hover:bg-[var(--surface-2)]"
          >
            {t('common.edit')}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs px-2 py-1 rounded border text-[color:var(--danger)] hover:bg-[var(--danger-soft)]"
          >
            {t('common.delete')}
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t bg-[var(--surface-2)]">
      <td className="px-3 py-2">
        <input
          type="number"
          value={draft.order}
          onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
          className="w-16 border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          className="w-full border rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <DueMeaningSelect
          value={draft.due_meaning}
          onChange={(due_meaning) => setDraft({ ...draft, due_meaning })}
        />
      </td>
      <td className="px-3 py-2 text-right space-x-2">
        <button
          type="button"
          onClick={() => {
            onUpdate({
              name: draft.name,
              order: draft.order,
              due_meaning: draft.due_meaning,
              ...(draft.description ? { description: draft.description } : {}),
            });
            setEditing(false);
          }}
          className="text-xs px-2 py-1 rounded bg-[var(--ink)] text-[color:var(--on-primary)]"
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-xs px-2 py-1 rounded border"
        >
          {t('common.cancel')}
        </button>
      </td>
    </tr>
  );
}

// --- Feature-Flags Admin -----------------------------------------------------

function FeatureFlagsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { items, isLoading } = useFeatureFlags();

  const setFlag = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.setFeatureFlag(key, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLAGS_QUERY_KEY });
      toast.success(t('admin.flags_saved', 'Feature-Flag aktualisiert'));
    },
    onError: (err) =>
      toast.error(t('admin.flags_save_failed', 'Speichern fehlgeschlagen'), (err as Error).message),
  });

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.flags_title', 'Feature-Flags')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.flags_subtitle',
            'Unfertige oder optionale Features zentral ein- und ausschalten. Ein deaktiviertes Flag wirkt global für alle Nutzer.',
          )}
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}

      {!isLoading && items.length === 0 && (
        <p className="text-sm text-[color:var(--ink-4)] italic">
          {t('admin.flags_empty', 'Keine Feature-Flags vorhanden.')}
        </p>
      )}

      {items.length > 0 && (
        <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg divide-y divide-[color:var(--line-soft)]">
          {items.map((flag) => (
            <div key={flag.key} className="flex items-center gap-4 px-4 py-3">
              <div className="min-w-0 flex-1">
                <code className="text-sm font-medium text-[color:var(--ink)]">{flag.key}</code>
                {flag.description && (
                  <p className="text-xs text-[color:var(--ink-3)] mt-0.5">{flag.description}</p>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={flag.enabled}
                aria-label={flag.key}
                disabled={setFlag.isPending}
                onClick={() => setFlag.mutate({ key: flag.key, enabled: !flag.enabled })}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  flag.enabled ? 'bg-[var(--primary)]' : 'bg-[var(--surface-3)]'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-[var(--surface)] shadow-sm transition-transform ${
                    flag.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="w-16 text-right text-xs text-[color:var(--ink-3)]">
                {flag.enabled ? t('admin.flags_on', 'an') : t('admin.flags_off', 'aus')}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// Benachrichtigungen: Vorlagen-Editor + Regel-Tabelle (#11 WP-7)
// ===========================================================================

const NOTIF_LOCALES: NotificationLocale[] = ['de', 'en'];
const NOTIF_CHANNELS: NotificationChannel[] = ['immediate', 'digest', 'off'];
const NOTIF_CADENCES: NotificationCadence[] = ['once', 'weekly', 'daily'];
const NOTIF_RECIPIENT_ROLES: NotificationRecipientRole[] = [
  'project_lead',
  'manager',
  'mtb',
  'responsible',
  'mentioned',
];

function NotificationsAdmin() {
  const { t } = useTranslation();
  return (
    <section className="space-y-10">
      <NotificationTemplatesEditor />
      <NotificationRulesTable />
      <DigestSettingsCard />
      <ProjektPruefungenAdmin />
      <p className="text-xs text-[color:var(--ink-4)]">
        {t(
          'admin.notifications_footnote',
          'Kern-Vorlagen können angepasst und zurückgesetzt, aber nicht gelöscht werden.',
        )}
      </p>
    </section>
  );
}

// ===========================================================================
// Digest-Versand (Tagesübersicht) steuerbar (#20)
// ===========================================================================

// Anzeige-Reihenfolge Mo…So; Werte nach JS Date.getDay() (0=So … 6=Sa).
const DIGEST_WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

function DigestSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const settingsQuery = useQuery({
    queryKey: ['notif-digest-settings'],
    queryFn: api.getDigestSettings,
  });

  const [draft, setDraft] = useState<DigestSettingsInput | null>(null);
  useEffect(() => {
    if (settingsQuery.data && draft === null) {
      const { enabled, time, weekdays } = settingsQuery.data;
      setDraft({ enabled, time, weekdays });
    }
  }, [settingsQuery.data, draft]);

  const save = useMutation({
    mutationFn: (body: DigestSettingsInput) => api.updateDigestSettings(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-digest-settings'] });
      toast.success(t('admin.notifications_digest_saved', 'Digest-Einstellungen gespeichert'));
    },
    onError: (err) =>
      toast.error(
        t('admin.notifications_digest_save_failed', 'Speichern fehlgeschlagen'),
        (err as Error).message,
      ),
  });

  const weekdayLabels: Record<number, string> = {
    0: t('admin.weekday_short_sun', 'So'),
    1: t('admin.weekday_short_mon', 'Mo'),
    2: t('admin.weekday_short_tue', 'Di'),
    3: t('admin.weekday_short_wed', 'Mi'),
    4: t('admin.weekday_short_thu', 'Do'),
    5: t('admin.weekday_short_fri', 'Fr'),
    6: t('admin.weekday_short_sat', 'Sa'),
  };

  const toggleWeekday = (day: number) =>
    setDraft((d) =>
      d === null
        ? d
        : {
            ...d,
            weekdays: d.weekdays.includes(day)
              ? d.weekdays.filter((w) => w !== day)
              : [...d.weekdays, day],
          },
    );

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.notifications_digest_title', 'Tagesübersicht (Digest)')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.notifications_digest_subtitle',
            'Steuert den gebündelten Versand aller Regeln mit Kanal „digest". Deaktiviert bleiben fällige Einträge liegen und werden am nächsten aktiven Versandtag nachgeliefert.',
          )}
        </p>
      </div>

      {settingsQuery.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}

      {draft !== null && (
        <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm font-semibold text-[color:var(--ink)]">digest_daily</code>
            <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
              {t('admin.notifications_rule_active', 'Aktiv')}
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) =>
                  setDraft((d) => (d === null ? d : { ...d, enabled: e.target.checked }))
                }
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
              {t('admin.notifications_digest_time', 'Uhrzeit')}
              <input
                type="time"
                value={draft.time}
                onChange={(e) => setDraft((d) => (d === null ? d : { ...d, time: e.target.value }))}
                className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
              />
              {settingsQuery.data && (
                <span className="text-[color:var(--ink-4)]">({settingsQuery.data.timezone})</span>
              )}
            </label>

            <div className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
              {t('admin.notifications_digest_weekdays', 'Versandtage')}
              <div className="flex gap-1">
                {DIGEST_WEEKDAY_ORDER.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleWeekday(day)}
                    className={`rounded-md border px-2 py-1 text-xs ${
                      draft.weekdays.includes(day)
                        ? 'border-[color:var(--primary)] bg-[var(--primary)] font-medium text-white'
                        : 'border-[color:var(--line)] bg-[var(--surface)] text-[color:var(--ink-3)]'
                    }`}
                  >
                    {weekdayLabels[day]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            {draft.weekdays.length === 0 && (
              <span className="text-xs text-[color:var(--warn,#b45309)]">
                {t(
                  'admin.notifications_digest_no_weekday',
                  'Mindestens ein Versandtag nötig — zum Abschalten den Aktiv-Schalter nutzen.',
                )}
              </span>
            )}
            <button
              type="button"
              onClick={() => save.mutate(draft)}
              disabled={save.isPending || draft.weekdays.length === 0}
              className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('admin.notifications_rule_save', 'Speichern')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

type TemplateDraft = {
  subject: string;
  intro_body: string;
  cta_label: string;
  footer: string;
  digest_summary: string;
};

function NotificationTemplatesEditor() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const templatesQuery = useQuery({
    queryKey: ['notif-templates'],
    queryFn: api.listNotificationTemplates,
  });

  const items = templatesQuery.data?.items ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [locale, setLocale] = useState<NotificationLocale>('de');
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [preview, setPreview] = useState<{ html: string; subject: string } | null>(null);

  const distinctKeys = useMemo(() => {
    const seen: string[] = [];
    for (const it of items) if (!seen.includes(it.key)) seen.push(it.key);
    return seen;
  }, [items]);

  const effectiveKey = selectedKey ?? distinctKeys[0] ?? null;
  const current: NotificationTemplateListItem | undefined = items.find(
    (it) => it.key === effectiveKey && it.locale === locale,
  );

  // Draft aus dem geladenen Datensatz initialisieren, sobald sich Key/Locale ändert.
  const draftSignature = `${effectiveKey}:${locale}:${current?.updated_at ?? 'def'}`;
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);
  if (current && loadedSignature !== draftSignature) {
    setLoadedSignature(draftSignature);
    setDraft({
      subject: current.subject,
      intro_body: current.intro_body,
      cta_label: current.cta_label,
      footer: current.footer,
      digest_summary: current.digest_summary ?? '',
    });
    setPreview(null);
  }

  const save = useMutation({
    mutationFn: () => {
      if (!effectiveKey || !draft) throw new Error('no_draft');
      return api.updateNotificationTemplate(effectiveKey, locale, {
        subject: draft.subject,
        intro_body: draft.intro_body,
        cta_label: draft.cta_label,
        footer: draft.footer,
        digest_summary: draft.digest_summary.trim() === '' ? null : draft.digest_summary,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-templates'] });
      setLoadedSignature(null);
      toast.success(t('admin.notifications_template_saved', 'Vorlage gespeichert'));
    },
    onError: (err) => {
      const e = err as ApiErrorLike;
      const unknown = e.payload?.unknown;
      if (Array.isArray(unknown) && unknown.length > 0) {
        toast.error(
          t('admin.notifications_unknown_placeholders', 'Unbekannte Platzhalter'),
          unknown.map((u) => `{{${u}}}`).join(', '),
        );
        return;
      }
      toast.error(
        t('admin.notifications_save_failed', 'Speichern fehlgeschlagen'),
        (err as Error).message,
      );
    },
  });

  const reset = useMutation({
    mutationFn: () => {
      if (!effectiveKey) throw new Error('no_key');
      return api.resetNotificationTemplate(effectiveKey, locale);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-templates'] });
      setLoadedSignature(null);
      toast.success(t('admin.notifications_template_reset', 'Auf Default zurückgesetzt'));
    },
    onError: (err) =>
      toast.error(
        t('admin.notifications_reset_failed', 'Zurücksetzen fehlgeschlagen'),
        (err as Error).message,
      ),
  });

  const doPreview = useMutation({
    mutationFn: () => {
      if (!effectiveKey || !draft) throw new Error('no_draft');
      return api.previewNotificationTemplate(effectiveKey, locale, {
        subject: draft.subject,
        intro_body: draft.intro_body,
        cta_label: draft.cta_label,
        footer: draft.footer,
        digest_summary: draft.digest_summary.trim() === '' ? null : draft.digest_summary,
      });
    },
    onSuccess: (res) => setPreview({ html: res.html, subject: res.subject }),
    onError: (err) =>
      toast.error(
        t('admin.notifications_preview_failed', 'Vorschau fehlgeschlagen'),
        (err as Error).message,
      ),
  });

  const testSend = useMutation({
    mutationFn: () => {
      if (!effectiveKey) throw new Error('no_key');
      return api.testSendNotificationTemplate(effectiveKey, locale);
    },
    onSuccess: () => toast.success(t('admin.notifications_test_sent', 'Testmail versendet')),
    onError: (err) =>
      toast.error(
        t('admin.notifications_test_failed', 'Testmail fehlgeschlagen'),
        (err as Error).message,
      ),
  });

  const field = (key: keyof TemplateDraft, value: string) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.notifications_templates_title', 'Mail-Vorlagen')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.notifications_templates_subtitle',
            'Betreff, Text und Beschriftungen der automatischen Mails anpassen. Platzhalter in geschweiften Klammern werden beim Versand ersetzt.',
          )}
        </p>
      </div>

      {templatesQuery.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}

      {!templatesQuery.isLoading && effectiveKey && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
              {t('admin.notifications_template_label', 'Vorlage')}
              <select
                value={effectiveKey}
                onChange={(e) => {
                  setSelectedKey(e.target.value);
                  setLoadedSignature(null);
                }}
                className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
              >
                {distinctKeys.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
              {t('admin.notifications_locale_label', 'Sprache')}
              <select
                value={locale}
                onChange={(e) => {
                  setLocale(e.target.value as NotificationLocale);
                  setLoadedSignature(null);
                }}
                className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
              >
                {NOTIF_LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {l.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                current?.is_overridden
                  ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                  : 'bg-[var(--surface-3)] text-[color:var(--ink-3)]'
              }`}
            >
              {current?.is_overridden
                ? t('admin.notifications_status_custom', 'angepasst')
                : t('admin.notifications_status_default', 'Default')}
            </span>
          </div>

          {current && (
            <p className="text-xs text-[color:var(--ink-4)]">
              {t('admin.notifications_placeholders_doc', 'Erlaubte Platzhalter:')}{' '}
              {current.placeholders.map((p) => (
                <code
                  key={p}
                  className="mr-1 rounded bg-[var(--surface-3)] px-1 py-0.5 text-[11px] text-[color:var(--ink)]"
                >{`{{${p}}}`}</code>
              ))}
            </p>
          )}

          {draft && (
            <div className="space-y-3 rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4">
              <TemplateField
                label={t('admin.notifications_field_subject', 'Betreff')}
                value={draft.subject}
                onChange={(v) => field('subject', v)}
              />
              <TemplateTextarea
                label={t('admin.notifications_field_intro', 'Textkörper')}
                value={draft.intro_body}
                onChange={(v) => field('intro_body', v)}
                rows={5}
              />
              <TemplateField
                label={t('admin.notifications_field_cta', 'Button-Beschriftung')}
                value={draft.cta_label}
                onChange={(v) => field('cta_label', v)}
              />
              <TemplateField
                label={t('admin.notifications_field_footer', 'Fußzeile')}
                value={draft.footer}
                onChange={(v) => field('footer', v)}
              />
              <TemplateTextarea
                label={t('admin.notifications_field_digest', 'Digest-Zeile (optional)')}
                value={draft.digest_summary}
                onChange={(v) => field('digest_summary', v)}
                rows={2}
              />

              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  disabled={save.isPending}
                  onClick={() => save.mutate()}
                  className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  {t('admin.notifications_save', 'Speichern')}
                </button>
                <button
                  type="button"
                  disabled={doPreview.isPending}
                  onClick={() => doPreview.mutate()}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm text-[color:var(--ink)] disabled:opacity-50"
                >
                  {t('admin.notifications_preview', 'Vorschau')}
                </button>
                <button
                  type="button"
                  disabled={testSend.isPending}
                  onClick={() => testSend.mutate()}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm text-[color:var(--ink)] disabled:opacity-50"
                >
                  {t('admin.notifications_test_send', 'Testmail an mich')}
                </button>
                <button
                  type="button"
                  disabled={reset.isPending || !current?.is_overridden}
                  onClick={() => reset.mutate()}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm text-[color:var(--ink-3)] disabled:opacity-40"
                >
                  {t('admin.notifications_reset', 'Auf Default zurücksetzen')}
                </button>
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-2 rounded-lg border border-[color:var(--line)] bg-[var(--surface-2)] p-4">
              <p className="text-xs font-medium text-[color:var(--ink-3)]">
                {t('admin.notifications_preview_subject', 'Betreff')}:{' '}
                <span className="text-[color:var(--ink)]">{preview.subject}</span>
              </p>
              <iframe
                title={t('admin.notifications_preview_title', 'Mail-Vorschau')}
                sandbox=""
                srcDoc={preview.html}
                className="h-64 w-full rounded border border-[color:var(--line)] bg-white"
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function TemplateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
      {label}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
      />
    </label>
  );
}

function TemplateTextarea({
  label,
  value,
  onChange,
  rows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
      {label}
      <textarea
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)] font-mono"
      />
    </label>
  );
}

type RuleDraft = {
  channel: NotificationChannel;
  cadence: NotificationCadence;
  first_immediate: boolean;
  recipients: NotificationRecipientRole[];
  template_key: string;
  is_active: boolean;
};

function NotificationRulesTable() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const rulesQuery = useQuery({ queryKey: ['notif-rules'], queryFn: api.listNotificationRules });
  const templatesQuery = useQuery({
    queryKey: ['notif-templates'],
    queryFn: api.listNotificationTemplates,
  });

  const templateKeys = useMemo(() => {
    const seen: string[] = [];
    for (const it of templatesQuery.data?.items ?? [])
      if (!seen.includes(it.key)) seen.push(it.key);
    return seen;
  }, [templatesQuery.data]);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.notifications_rules_title', 'Versand-Regeln')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.notifications_rules_subtitle',
            'Je Ereignis: Kanal, Takt, Empfänger und Vorlage. Reminder-Empfänger folgen einer festen Strategie und sind nicht editierbar.',
          )}
        </p>
      </div>

      {rulesQuery.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}

      {!rulesQuery.isLoading && (
        <div className="space-y-3">
          {(rulesQuery.data?.items ?? []).map((rule) => (
            <RuleRow
              key={rule.event_type}
              rule={rule}
              templateKeys={templateKeys}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['notif-rules'] });
                toast.success(t('admin.notifications_rule_saved', 'Regel gespeichert'));
              }}
              onError={(msg) =>
                toast.error(
                  t('admin.notifications_rule_save_failed', 'Speichern fehlgeschlagen'),
                  msg,
                )
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RuleRow({
  rule,
  templateKeys,
  onSaved,
  onError,
}: {
  rule: NotificationRuleListItem;
  templateKeys: string[];
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const isStrategy = rule.recipient_strategy !== null;
  const [draft, setDraft] = useState<RuleDraft>({
    channel: rule.channel,
    cadence: rule.cadence,
    first_immediate: rule.first_immediate,
    recipients: rule.recipients,
    template_key: rule.template_key,
    is_active: rule.is_active,
  });

  const save = useMutation({
    mutationFn: () => api.updateNotificationRule(rule.event_type, draft),
    onSuccess: onSaved,
    onError: (err) => onError((err as Error).message),
  });

  const toggleRecipient = (role: NotificationRecipientRole) =>
    setDraft((d) => ({
      ...d,
      recipients: d.recipients.includes(role)
        ? d.recipients.filter((r) => r !== role)
        : [...d.recipients, role],
    }));

  return (
    <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <code className="text-sm font-semibold text-[color:var(--ink)]">{rule.event_type}</code>
        <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
          {t('admin.notifications_rule_active', 'Aktiv')}
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
          {t('admin.notifications_rule_channel', 'Kanal')}
          <select
            value={draft.channel}
            onChange={(e) =>
              setDraft((d) => ({ ...d, channel: e.target.value as NotificationChannel }))
            }
            className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
          >
            {NOTIF_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
          {t('admin.notifications_rule_cadence', 'Takt')}
          <select
            value={draft.cadence}
            onChange={(e) =>
              setDraft((d) => ({ ...d, cadence: e.target.value as NotificationCadence }))
            }
            className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
          >
            {NOTIF_CADENCES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)]">
          {t('admin.notifications_rule_template', 'Vorlage')}
          <select
            value={draft.template_key}
            onChange={(e) => setDraft((d) => ({ ...d, template_key: e.target.value }))}
            className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
          >
            {templateKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label
          className="flex items-center gap-2 text-xs text-[color:var(--ink-3)] pt-5"
          title={t(
            'admin.notifications_rule_first_immediate_hint',
            'Wird erst in einer späteren Ausbaustufe wirksam — aktuell nur gespeichert.',
          )}
        >
          <input
            type="checkbox"
            checked={draft.first_immediate}
            onChange={(e) => setDraft((d) => ({ ...d, first_immediate: e.target.checked }))}
          />
          {t('admin.notifications_rule_first_immediate', 'Erste sofort')}
          <span className="opacity-60">
            {t('admin.notifications_rule_first_immediate_badge', '(später)')}
          </span>
        </label>
      </div>

      <div>
        <p className="text-xs text-[color:var(--ink-3)] mb-1">
          {t('admin.notifications_rule_recipients', 'Empfänger')}
        </p>
        {isStrategy ? (
          <p className="text-xs italic text-[color:var(--ink-4)]">
            {t('admin.notifications_rule_strategy', 'Feste Strategie')}:{' '}
            <code className="not-italic text-[color:var(--ink-3)]">{rule.recipient_strategy}</code>{' '}
            {t('admin.notifications_rule_strategy_hint', '(nicht editierbar)')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {NOTIF_RECIPIENT_ROLES.map((role) => (
              <label
                key={role}
                className="flex items-center gap-1.5 rounded-md border border-[color:var(--line)] px-2 py-1 text-xs text-[color:var(--ink-3)]"
              >
                <input
                  type="checkbox"
                  checked={draft.recipients.includes(role)}
                  onChange={() => toggleRecipient(role)}
                />
                {role}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {t('admin.notifications_rule_save', 'Speichern')}
        </button>
      </div>
    </div>
  );
}

/** Minimaler Typ für ApiError-Payload mit Platzhalter-Liste (422). */
type ApiErrorLike = { payload?: { unknown?: unknown }; message: string };

// --- Systemprompts Admin (PV-003) --------------------------------------------

function PromptsAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const promptsQuery = useQuery({ queryKey: ['admin-prompts'], queryFn: api.listPrompts });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.user_id, u.name);
    return m;
  }, [usersQuery.data]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] });

  const update = useMutation({
    mutationFn: ({ key, content }: { key: string; content: string }) =>
      api.updatePrompt(key, content),
    onSuccess: () => {
      invalidate();
      toast.success(t('admin.prompts_saved', 'Prompt gespeichert'));
    },
    onError: (err) =>
      toast.error(
        t('admin.prompts_save_failed', 'Prompt konnte nicht gespeichert werden'),
        err instanceof Error ? err.message : String(err),
      ),
  });
  const reset = useMutation({
    mutationFn: (key: string) => api.resetPrompt(key),
    onSuccess: () => {
      invalidate();
      toast.success(t('admin.prompts_reset_done', 'Auf Default zurückgesetzt'));
    },
    onError: (err) =>
      toast.error(
        t('admin.prompts_reset_failed', 'Zurücksetzen fehlgeschlagen'),
        err instanceof Error ? err.message : String(err),
      ),
  });

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.prompts_title', 'Systemprompts')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.prompts_subtitle',
            'Die als Datei ausgelieferten KI-Systemprompts pro Key überschreiben. Änderungen wirken sofort auf folgende KI-Aufrufe, ohne Redeploy. „Auf Default zurücksetzen“ verwirft die Anpassung.',
          )}
        </p>
      </div>

      {promptsQuery.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}
      {!promptsQuery.isLoading && (promptsQuery.data?.items.length ?? 0) === 0 && (
        <p className="text-sm text-[color:var(--ink-4)] italic">
          {t('admin.prompts_empty', 'Keine Systemprompts vorhanden.')}
        </p>
      )}

      <div className="space-y-3">
        {promptsQuery.data?.items.map((p) => (
          <PromptCard
            key={p.key}
            item={p}
            authorName={p.updated_by ? (userNameById.get(p.updated_by) ?? null) : null}
            onSave={(content) => update.mutate({ key: p.key, content })}
            onReset={() => reset.mutate(p.key)}
            isSaving={update.isPending}
            isResetting={reset.isPending}
          />
        ))}
      </div>
    </section>
  );
}

function PromptCard({
  item,
  authorName,
  onSave,
  onReset,
  isSaving,
  isResetting,
}: {
  item: PromptListItem;
  authorName: string | null;
  onSave: (content: string) => void;
  onReset: () => void;
  isSaving: boolean;
  isResetting: boolean;
}) {
  const { t, i18n } = useTranslation();
  const confirmAction = useConfirm();
  const [draft, setDraft] = useState(item.content);
  const [showDefault, setShowDefault] = useState(false);

  // Server-Stand hat sich geändert (z. B. nach Save/Reset) → Draft nachziehen.
  // Der Key-prop remountet bei Listenwechsel; dieser Effekt deckt In-place-Updates
  // desselben Keys (Save/Reset) ab.
  useEffect(() => {
    setDraft(item.content);
  }, [item.content]);

  const trimmed = draft.trim();
  const canSave = trimmed.length > 0 && draft !== item.content && !isSaving;
  const dateLabel = item.updated_at
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(item.updated_at),
      )
    : null;

  return (
    <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-sm font-medium text-[color:var(--ink)]">{item.key}</code>
        {item.is_overridden ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--warn-soft)] text-[color:var(--warn)] font-medium uppercase tracking-wide">
            {t('admin.prompts_status_overridden', 'angepasst')}
          </span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-3)] text-[color:var(--ink-3)] font-medium uppercase tracking-wide">
            {t('admin.prompts_status_default', 'Default')}
          </span>
        )}
      </div>

      <label className="block">
        <span className="text-[11px] uppercase tracking-wide text-[color:var(--ink-3)] block mb-1">
          {t('admin.prompts_editor_label', 'Aktiver Prompt')}
        </span>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          className="input w-full font-mono text-xs leading-relaxed"
        />
      </label>

      <div>
        <button
          type="button"
          onClick={() => setShowDefault((v) => !v)}
          className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
        >
          {showDefault
            ? t('admin.prompts_hide_default', 'Default ausblenden')
            : t('admin.prompts_show_default', 'Default zum Vergleich anzeigen')}
        </button>
        {showDefault &&
          (item.is_overridden ? (
            <div className="mt-2">
              <div className="text-[11px] uppercase tracking-wide text-[color:var(--ink-3)] block mb-1">
                {t('admin.prompts_default_label', 'Ausgelieferter Default (Read-only)')}
              </div>
              <pre className="text-[11px] bg-[var(--surface-2)] p-2 rounded max-h-64 overflow-auto whitespace-pre-wrap text-[color:var(--ink-2)]">
                {item.default_content}
              </pre>
            </div>
          ) : (
            <p className="mt-2 text-xs text-[color:var(--ink-4)] italic">
              {t(
                'admin.prompts_default_is_active',
                'Dieser Prompt entspricht dem Default — der aktive Inhalt oben ist bereits der Default.',
              )}
            </p>
          ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => canSave && onSave(trimmed)}
          disabled={!canSave}
          className="btn-primary"
        >
          {t('admin.prompts_save', 'Speichern')}
        </button>
        <button
          type="button"
          onClick={() => {
            void confirmAction({
              title: t(
                'admin.prompts_reset_confirm',
                'Diesen Prompt auf den Default zurücksetzen?',
              ),
              confirmLabel: t('admin.prompts_reset', 'Auf Default zurücksetzen'),
            }).then((ok) => {
              if (ok) onReset();
            });
          }}
          disabled={!item.is_overridden || isResetting}
          className="text-xs px-3 py-1.5 rounded border border-[color:var(--line)] text-[color:var(--ink-2)] hover:text-[color:var(--ink)] disabled:opacity-40"
        >
          {t('admin.prompts_reset', 'Auf Default zurücksetzen')}
        </button>
        <span className="ml-auto text-[11px] text-[color:var(--ink-4)]">
          {item.is_overridden && dateLabel
            ? t('admin.prompts_last_changed', 'Zuletzt geändert von {{name}} am {{date}}', {
                name: authorName ?? '—',
                date: dateLabel,
              })
            : t('admin.prompts_never_changed', 'Unverändert (Default)')}
        </span>
      </div>
    </div>
  );
}
